import React, { useEffect, useRef } from 'react'

import type { CompletionItem } from './types'

// Generic, presentational completion list. Emoji-agnostic: it renders items,
// highlights the active row, keeps it scrolled into view, and reports
// click/hover. Keyboard handling and data live in ComposerMessageInput so a
// future @mention menu reuses this untouched.

type Props = {
  ariaLabel?: string
  items: CompletionItem[]
  active: number
  onSelect: (item: CompletionItem) => void
  onHover: (index: number) => void
}

export function CompletionMenu({
  ariaLabel,
  items,
  active,
  onSelect,
  onHover,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const row = listRef.current?.children[active] as HTMLElement | undefined
    row?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (items.length === 0) {
    return null
  }

  return (
    <div
      className='completion-menu'
      role='listbox'
      aria-label={ariaLabel}
      ref={listRef}
    >
      {items.map((it, i) => (
        <div
          key={it.id}
          role='option'
          aria-selected={i === active}
          className={'completion-menu-item' + (i === active ? ' active' : '')}
          onMouseEnter={() => onHover(i)}
          // mousedown (not click) so the textarea keeps focus and no blur fires
          onMouseDown={e => {
            e.preventDefault()
            onSelect(it)
          }}
        >
          {it.preview && (
            <span className='completion-menu-preview'>{it.preview}</span>
          )}
          <span className='completion-menu-label'>{it.label}</span>
        </div>
      ))}
    </div>
  )
}
